import { useState, useCallback } from 'react';

const SWITCH_TIMEOUT_MS = 1200000;
const REFRESH_DELAY_MS = 5000;

interface ServerStatus {
  instanceId: string;
  currentType: string;
  currentState: string;
  upgradeType: string;
  downgradeType: string;
  canUpgrade: boolean;
  canDowngrade: boolean;
  message: string;
}

export const useMailServer = (managementUrl: string) => {
  const [status, setStatus] = useState<ServerStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const fetchStatus = useCallback(async () => {
    if (!managementUrl) return;
    
    setLoading(true);
    setError('');
    setSuccessMessage('');
    
    try {
      const response = await fetch(managementUrl);
      if (!response.ok) throw new Error('Failed to fetch server status');
      const data = await response.json();
      setStatus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch status');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, [managementUrl]);

  const switchInstance = useCallback(async (action: 'upgrade' | 'downgrade') => {
    if (!managementUrl) return;

    setSwitching(true);
    setError('');
    setSuccessMessage('');

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), SWITCH_TIMEOUT_MS);
      
      const response = await fetch(`${managementUrl}?switch=${action}`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      if (!response.ok) throw new Error(`Failed to ${action} server`);
      const data = await response.json();
      setSuccessMessage(data.message || `Server ${action}d successfully`);
      
      setTimeout(() => {
        fetchStatus();
      }, REFRESH_DELAY_MS);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        setError('Request timeout. Please check server status manually.');
      } else {
        setError(err instanceof Error ? err.message : `Failed to ${action} server`);
      }
    } finally {
      setSwitching(false);
    }
  }, [managementUrl, fetchStatus]);

  return {
    status,
    loading,
    switching,
    error,
    successMessage,
    fetchStatus,
    switchInstance,
    setError,
    setSuccessMessage,
  };
};
